import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, ChevronUpIcon, XIcon, PhoneIcon } from 'lucide-react';
import { FunnelProvider, STEPS, STEP_ESTIMATE, STEP_RESERVE, STEP_STATUS, useFunnel } from '../contexts/FunnelContext';
import { FunnelHeader } from '../components/funnel/FunnelHeader';
import { HomeLanding } from '../components/funnel/HomeLanding';
import { ProgressBar } from '../components/funnel/ProgressBar';
import { StepSize } from '../components/funnel/StepSize';
import { StepAddresses } from '../components/funnel/StepAddresses';
import { StepAccess } from '../components/funnel/StepAccess';
import { StepInventory } from '../components/funnel/StepInventory';
import { StepPacking } from '../components/funnel/StepPacking';
import { StepDate } from '../components/funnel/StepDate';
import { StepContact } from '../components/funnel/StepContact';
import { StepEstimate } from '../components/funnel/StepEstimate';
import { StepReserve } from '../components/funnel/StepReserve';
import { StepStatus } from '../components/funnel/StepStatus';
import { QuoteSummary } from '../components/funnel/QuoteSummary';
import { stepErrors, type StepErrors } from '../utils/validation';
import { hrs } from '../utils/format';
import { phones, siteConfig } from '../data/siteConfig';
import { firePageView } from '../utils/metaPixel';
const completionEvents: Record<number, string> = {
  1: 'AddressCompleted',
  2: 'AccessCompleted',
  3: 'InventoryCompleted'
};
function FunnelFlow() {
  const {
    step,
    goTo,
    data,
    t,
    track,
    quote,
    submitted,
    acceptEstimate
  } = useFunnel();
  const [attempted, setAttempted] = useState<Record<number, boolean>>({});
  const [sheet, setSheet] = useState(false);
  const errors: StepErrors = attempted[step] ? stepErrors(step, data) : {};
  const showNav = step <= STEP_ESTIMATE;
  const next = () => {
    const e = stepErrors(step, data);
    if (Object.keys(e).length) {
      setAttempted(a => ({
        ...a,
        [step]: true
      }));
      return;
    }
    if (completionEvents[step]) track(completionEvents[step], {
      step
    });
    if (step === STEP_ESTIMATE) acceptEstimate();
    goTo(step + 1);
  };
  const content = [<StepSize key="s" errors={errors} data-id="element-1112" data-mp="3igm6op3sjetx" />, <StepAddresses key="a" errors={errors} data-id="element-1113" data-mp="3djdporudpgu9" />, <StepAccess key="ac" data-id="element-1114" data-mp="3dkeysqru3t9c" />, <StepInventory key="i" errors={errors} data-id="element-1115" data-mp="2pu1936wq26hx" />, <StepPacking key="p" errors={errors} data-id="element-1116" data-mp="2puuh0b7f321n" />, <StepDate key="d" errors={errors} data-id="element-1117" data-mp="2proun3q6ae9m" />, <StepContact key="c" errors={errors} data-id="element-1118" data-mp="2lthadywi2uto" />, <StepEstimate key="e" data-id="element-1119" data-mp="2lqd0ckwct4v3" />, <StepReserve key="r" data-id="element-1120" data-mp="2zjkscdock2db" />, <StepStatus key="st" data-id="element-1121" data-mp="2zkw4p1vuyig1" />][step];
  return <>
      <ProgressBar data-id="element-1122" data-mp="2ut4fauofuirh" />
      <div className="mx-auto grid max-w-6xl gap-10 px-4 pb-32 pt-8 sm:px-6 lg:grid-cols-[1fr_320px] lg:pb-16 lg:pt-10" data-id="element-1123" data-mp="2usm7r1wbsi49">
        <main data-id="element-1124" data-mp="2728pu47gfzne">
          <AnimatePresence mode="wait" initial={false} data-id="element-1125" data-mp="271yyodaobmg4">
            <motion.div key={step} initial={{
            opacity: 0,
            x: 12
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -12
          }} transition={{
            duration: 0.18,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-1126" data-mp="22507o3mb3vm5">
              {content}
            </motion.div>
          </AnimatePresence>

          {showNav && <div className="fixed inset-x-0 bottom-0 z-20 border-t border-ink-200 bg-white/95 px-4 py-3 backdrop-blur lg:static lg:mt-12 lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-0" data-id="element-1127" data-mp="8lzvff2xqtaf">
              <div className="mx-auto flex max-w-6xl items-center gap-3" data-id="element-1128" data-mp="11aon6ni3pm9i">
                {step > 0 && <button type="button" onClick={() => goTo(step - 1)} className="flex h-14 items-center gap-2 rounded-xl border-2 border-ink-200 bg-white px-4 font-semibold text-ink transition-[border-color] duration-150 ease-out hover:border-ink" data-id="element-1129" data-mp="15cilwq8qkg71">
                    <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-1130" data-mp="3c1othycsszym" />
                    <span className="hidden sm:inline" data-id="element-1131" data-mp="3h1tbjstcu2qy">{t('back')}</span>
                  </button>}
                {data.bedrooms && <button type="button" onClick={() => setSheet(true)} className="hidden h-14 flex-col items-start justify-center rounded-xl bg-ink-100 px-3 text-left min-[420px]:flex lg:hidden" aria-label={t('summary')} data-id="element-1132" data-mp="7wufzfwoasa">
                    <span className="flex items-center gap-1 text-[11px] font-medium text-ink-500" data-id="element-1133" data-mp="3ijru57thjotv">
                      {t('summary')} <ChevronUpIcon className="h-3 w-3" aria-hidden="true" data-id="element-1134" data-mp="j56d3edepk8h" />
                    </span>
                    <span className="whitespace-nowrap text-sm font-bold text-ink" data-id="element-1135" data-mp="f2t8itaebsda">
                      {quote.crew.used} · {hrs(quote.serviceLow)}–{hrs(quote.serviceHigh)}h
                    </span>
                  </button>}
                <a href={phones.ai.href} aria-label={`${t('callKyle')} ${phones.ai.display}`} className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border-2 border-ink-200 bg-white text-ink lg:hidden" data-id="element-1136" data-mp="54n5278xye6b">
                  <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-1137" data-mp="2ft0jjkf2z116" />
                </a>
                <button type="button" onClick={next} className="flex h-14 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-magenta px-5 text-[17px] font-extrabold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-magenta-dark active:scale-[0.98] sm:text-[19px] lg:max-w-sm" data-id="element-1138" data-mp="2kqs8q6occ91o">
                  {step === STEP_ESTIMATE ? t('acceptContinue') : t('next')}
                  <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1139" data-mp="38gweokp04kvu" />
                </button>
              </div>
            </div>}
          {step === STEP_RESERVE && <button type="button" onClick={() => goTo(step - 1)} className="mt-6 inline-flex min-h-[44px] items-center gap-2 font-semibold text-ink underline decoration-ink-300 underline-offset-4" data-id="element-1140" data-mp="1e2lni5kxpemw">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1141" data-mp="1azfiqz91v5kj" />
              {t('back')}
            </button>}
        </main>
        <aside className="hidden lg:block" data-id="element-1142" data-mp="162yve79ocv8i">
          <div className="sticky top-24" data-id="element-1143" data-mp="1ky4zgop7mjz7">
            <QuoteSummary onEdit={submitted || step === STEP_STATUS ? undefined : goTo} data-id="element-1144" data-mp="1kzzp2gbff09m" />
          </div>
        </aside>
      </div>

      <AnimatePresence data-id="element-1145" data-mp="1g1oe229eajpt">
        {sheet && <motion.div className="fixed inset-0 z-40 flex items-end bg-ink/50 lg:hidden" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={() => setSheet(false)} data-id="element-1146" data-mp="1ty1lsjo4n6r3">
            <motion.div role="dialog" aria-modal="true" aria-label={t('summary')} className="w-full p-3" initial={{
          y: 40,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 40,
          opacity: 0
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} onClick={e => e.stopPropagation()} data-id="element-1147" data-mp="23o4mz75ejr0i">
              <div className="relative" data-id="element-1148" data-mp="2wbe2jxe6plwp">
                <button type="button" onClick={() => setSheet(false)} aria-label={t('close')} className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-lg text-ink hover:bg-ink-100" data-id="element-1149" data-mp="2waberyc5rzl0">
                  <XIcon className="h-5 w-5" data-id="element-1150" data-mp="2n94vxim1ecol" />
                </button>
                <QuoteSummary onEdit={s => {
              setSheet(false);
              goTo(s);
            }} data-id="element-1151" data-mp="28ljq3gxs1zcz" />
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function FunnelShell() {
  const {
    started,
    t
  } = useFunnel();
  return <div className="min-h-screen w-full bg-paper" data-id="element-1152" data-mp="23kcjhl7df67w">
      <FunnelHeader data-id="element-1153" data-mp="1pnz4ciqb7gwl" />
      {started ? <FunnelFlow data-id="element-1154" data-mp="1pnpjtao1a1ya" /> : <HomeLanding data-id="element-1155" data-mp="1ppa84ijr8onp" />}
      <footer className="border-t border-ink-200 bg-paper" data-id="element-1156" data-mp="1ulr2vvlgm19r">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-6 text-sm text-ink-500 sm:px-6" data-id="element-1157" data-mp="1uo3kg7nb29ma">
          <div data-id="element-1158" data-mp="25576969y0d7p">
            <p data-id="element-1159" data-mp="254pcqheo0d3h">© 2026 IMF Tampa Movers · {t('footerScope')}</p>
            <p className="mt-1" data-id="element-1160" data-mp="2534d4ddhu3yr">
              {t('footerLegal')}
              {siteConfig.privacyUrl && <>
                  {' · '}
                  <a href={siteConfig.privacyUrl} className="underline underline-offset-4 hover:text-ink" data-id="element-1161" data-mp="1w3hdnx4br88c">Privacy Policy</a>
                </>}
              {siteConfig.smsTermsUrl && <>
                  {' · '}
                  <a href={siteConfig.smsTermsUrl} className="underline underline-offset-4 hover:text-ink" data-id="element-1162" data-mp="1hfcu6qxg6soi">SMS Terms</a>
                </>}
            </p>
          </div>
          <Link to="/admin" className="font-medium text-ink-600 underline underline-offset-4 hover:text-ink" data-id="element-1163" data-mp="1dbyb1re7pw6u">
            {t('footerStaff')}
          </Link>
        </div>
      </footer>
    </div>;
}
export function FunnelPage({
  creativeKey
}: {
  creativeKey: string;
}) {
  useEffect(() => {
    firePageView();
  }, []);
  const urlKey = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('creative_key') : null;
  return <FunnelProvider creativeKey={urlKey ?? creativeKey} data-id="element-1164" data-mp="3p2678r614z1z">
      <FunnelShell data-id="element-1165" data-mp="3t2q03ijklgib" />
    </FunnelProvider>;
}
export { STEPS };